/**
 * Phase 101df (Feb 2026) — Address book picker
 * Phase 101df-v2 (Feb 2026) — Ajout du chip « contact lié » + mini-carte au survol
 * ---------------------------------------------------------------------------
 * Composant unifié de sélection d'adresse depuis le carnet central (`/api/contacts`
 * agrège 6 sources : distributeurs automates, revendeurs, clients boutique, devis
 * traiteur, candidats revendeurs, destinataires de courriers).
 *
 * Usage :
 *   const [linked, setLinked] = useState(null);
 *   <AddressBookPicker
 *     onSelect={(c) => { update({recipient_name: c.name, ...}); setLinked(c); }}
 *     linkedContact={linked}
 *     onUnlink={() => setLinked(null)}
 *     preferredRoles={["COURRIER", "REVENDEUR"]}
 *   />
 *
 * Le composant NE modifie JAMAIS les champs du formulaire directement — c'est
 * au parent de mapper `contact` vers ses propres champs et de gérer l'état
 * du contact lié (le picker n'est pas responsable de la persistance).
 */
import { useEffect, useMemo, useState } from "react";
import { Book, ChevronsUpDown, FileText, Mail, MapPin, Phone, Search, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { api } from "@/lib/api";

const ROLE_LABELS = {
  AUTOMATE: { label: "Automate", color: "bg-[#EEF4F1] text-[#1F4E3D]" },
  REVENDEUR: { label: "Revendeur", color: "bg-[#FDF3E7] text-[#8B5A2B]" },
  B2C: { label: "Boutique", color: "bg-[#F0EDFA] text-[#5B4C99]" },
  TRAITEUR: { label: "Traiteur", color: "bg-[#FEEFE5] text-[#B84C1B]" },
  PROSPECT: { label: "Prospect", color: "bg-[#F5F5F1] text-[#575A57]" },
  COURRIER: { label: "Courrier", color: "bg-[#E9F0FA] text-[#2D5C8F]" },
};

/**
 * Formatte la date « dernier document » en français court.
 * @param {string} iso — ISO date ou date-only string
 * @returns {string}
 */
function formatShortDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
}

/**
 * Déduit le libellé « dernier document » à partir des méta-données de la fiche.
 * Ex : « Dernier courrier : Relance CRR-… du 12/02/2026 »
 */
function lastDocumentSummary(c) {
  if (!c) return null;
  if (c.last_letter_date) {
    const subj = (c.last_letter_subject || "").trim();
    const date = formatShortDate(c.last_letter_date);
    if (subj) return { label: "Dernier courrier", body: subj, date };
    return { label: "Dernier courrier", body: null, date };
  }
  if (c.last_event_date) {
    return { label: "Dernier événement traiteur", body: null, date: formatShortDate(c.last_event_date) };
  }
  if (c.last_login_at) {
    return { label: "Dernière connexion boutique", body: null, date: formatShortDate(c.last_login_at) };
  }
  return null;
}

/**
 * Mini-carte au survol : rôles + adresse complète + dernier document.
 */
function ContactHoverCardContent({ contact }) {
  const roles = contact?.roles || [];
  const last = lastDocumentSummary(contact);
  return (
    <HoverCardContent className="w-80 p-0" align="start" sideOffset={6}>
      <div className="p-3 border-b border-[#E5E5E0]">
        <div className="flex items-center gap-2 mb-1">
          <User className="w-3.5 h-3.5 text-[#1F4E3D] shrink-0" />
          <span className="text-sm font-semibold text-[#1A1C1A] truncate">
            {contact.name || <em className="text-[#A3A6A3]">Sans nom</em>}
          </span>
        </div>
        {contact.organization && (
          <div className="text-xs text-[#575A57] pl-5 truncate">{contact.organization}</div>
        )}
        {roles.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2 pl-5">
            {roles.map((r) => {
              const cfg = ROLE_LABELS[r] || { label: r, color: "bg-[#F5F5F1] text-[#575A57]" };
              return (
                <span
                  key={r}
                  className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${cfg.color}`}
                >
                  {cfg.label}
                </span>
              );
            })}
          </div>
        )}
      </div>
      <div className="p-3 space-y-1.5 text-xs">
        {(contact.address || contact.postal_code || contact.city) && (
          <div className="flex items-start gap-2 text-[#575A57]">
            <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
            <div className="min-w-0">
              {contact.address && <div>{contact.address}</div>}
              {(contact.postal_code || contact.city) && (
                <div>{[contact.postal_code, contact.city].filter(Boolean).join(" ")}</div>
              )}
            </div>
          </div>
        )}
        {contact.email && (
          <div className="flex items-center gap-2 text-[#575A57] truncate">
            <Mail className="w-3 h-3 shrink-0" />
            <span className="truncate">{contact.email}</span>
          </div>
        )}
        {contact.phone && (
          <div className="flex items-center gap-2 text-[#575A57]">
            <Phone className="w-3 h-3 shrink-0" />
            <span>{contact.phone}</span>
          </div>
        )}
      </div>
      {last && (
        <div className="border-t border-[#E5E5E0] p-3 bg-[#FAFAF7]">
          <div className="flex items-start gap-2 text-[11px]">
            <FileText className="w-3 h-3 mt-0.5 text-[#1F4E3D] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[#575A57]">{last.label} · {last.date}</div>
              {last.body && (
                <div className="text-[#1A1C1A] mt-0.5 truncate" title={last.body}>{last.body}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </HoverCardContent>
  );
}

/**
 * Chip « contact lié » — affiché juste après le bouton du picker quand un
 * contact a été sélectionné. Au survol → mini-carte détaillée.
 */
function LinkedContactChip({ contact, onUnlink }) {
  if (!contact) return null;
  const primary = contact.name || contact.organization || "Contact";
  const secondary = contact.city || contact.organization || "";
  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div
          className="inline-flex items-center gap-1.5 h-8 pl-2 pr-1 rounded-md border border-[#C3D9CC] bg-[#EEF4F1] text-xs cursor-default max-w-[240px]"
          data-testid="address-book-linked-chip"
        >
          <User className="w-3 h-3 text-[#1F4E3D] shrink-0" />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-[#1A1C1A] font-medium truncate">{primary}</span>
            {secondary && secondary !== primary && (
              <span className="text-[10px] text-[#575A57] truncate">{secondary}</span>
            )}
          </div>
          <button
            type="button"
            onClick={onUnlink}
            className="ml-1 rounded p-0.5 text-[#575A57] hover:bg-white hover:text-[#C05C47]"
            title="Délier ce contact"
            data-testid="address-book-linked-unlink"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </HoverCardTrigger>
      <ContactHoverCardContent contact={contact} />
    </HoverCard>
  );
}

/**
 * Address book picker.
 *
 * @param {(contact:Object) => void} onSelect  — appelé à la sélection ; reçoit le contact
 *                                               enrichi (rôles + dernières dates + …)
 * @param {Object|null} linkedContact          — contact actuellement lié (affiche le chip)
 * @param {() => void} onUnlink                — appelé quand l'user clique X sur le chip
 * @param {string} triggerLabel                — texte du bouton (défaut « Depuis le carnet »)
 * @param {string} testid                       — data-testid du bouton trigger
 * @param {string[]} preferredRoles            — rôles priorisés en haut de liste
 */
export function AddressBookPicker({
  onSelect,
  linkedContact = null,
  onUnlink,
  triggerLabel = "Depuis le carnet",
  testid = "address-book-picker-btn",
  preferredRoles = [],
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/contacts")
      .then(({ data }) => setContacts(Array.isArray(data?.items) ? data.items : []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = contacts;
    if (s) {
      const norm = (v) => (v || "").toString().toLowerCase();
      list = list.filter(
        (c) =>
          norm(c.name).includes(s) ||
          norm(c.organization).includes(s) ||
          norm(c.email).includes(s) ||
          norm(c.city).includes(s) ||
          norm(c.phone).includes(s),
      );
    }
    if (preferredRoles.length > 0) {
      list = [...list].sort((a, b) => {
        const aPref = (a.roles || []).some((r) => preferredRoles.includes(r)) ? 0 : 1;
        const bPref = (b.roles || []).some((r) => preferredRoles.includes(r)) ? 0 : 1;
        if (aPref !== bPref) return aPref - bPref;
        return (a.name || "").localeCompare(b.name || "");
      });
    }
    return list.slice(0, 200);
  }, [contacts, search, preferredRoles]);

  const handleSelect = (contact) => {
    // Le parent reçoit l'objet ENRICHI (roles, last_letter_date, sources, …).
    // Il extrait ce dont il a besoin pour ses champs de formulaire, et peut
    // stocker l'objet complet comme `linkedContact` pour afficher le chip.
    onSelect({
      name: contact.name || "",
      organization: contact.organization || "",
      email: contact.email || "",
      phone: contact.phone || "",
      address: contact.address || "",
      postal_code: contact.postal_code || "",
      city: contact.city || "",
      country: contact.country || "Suisse",
      roles: contact.roles || [],
      sources: contact.sources || [],
      last_letter_date: contact.last_letter_date || null,
      last_letter_subject: contact.last_letter_subject || null,
      last_event_date: contact.last_event_date || null,
      last_login_at: contact.last_login_at || null,
    });
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="text-xs h-8"
            data-testid={testid}
          >
            <Book className="w-3.5 h-3.5 mr-1.5" />
            {triggerLabel}
            <ChevronsUpDown className="w-3 h-3 ml-1.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[420px] p-0" align="start">
          <div className="p-2 border-b border-[#E5E5E0]">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-[#A3A6A3] absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, société, ville, email…"
                className="pl-8 h-8 text-sm"
                data-testid="address-book-picker-search"
              />
              {search && (
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#A3A6A3] hover:text-[#1A1C1A]"
                  onClick={() => setSearch("")}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-[360px] overflow-y-auto" data-testid="address-book-picker-list">
            {loading ? (
              <div className="p-4 text-center text-xs text-[#575A57]">Chargement…</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-xs text-[#575A57]">
                {search ? "Aucun contact ne correspond." : "Carnet vide."}
              </div>
            ) : (
              <ul className="py-1">
                {filtered.map((c, idx) => (
                  <li key={`${c.name}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => handleSelect(c)}
                      className="w-full text-left px-3 py-2 hover:bg-[#F5F5F1] transition-colors border-b border-[#F5F5F1] last:border-b-0"
                      data-testid={`address-book-picker-item-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1A1C1A] truncate">
                            {c.name || <em className="text-[#A3A6A3]">Sans nom</em>}
                          </div>
                          {c.organization && (
                            <div className="text-xs text-[#575A57] truncate">{c.organization}</div>
                          )}
                          <div className="text-[11px] text-[#A3A6A3] truncate">
                            {[c.address, c.postal_code, c.city].filter(Boolean).join(", ") || "—"}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {(c.roles || []).slice(0, 2).map((r) => {
                            const cfg = ROLE_LABELS[r] || { label: r, color: "bg-[#F5F5F1] text-[#575A57]" };
                            return (
                              <span
                                key={r}
                                className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${cfg.color}`}
                              >
                                {cfg.label}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border-t border-[#E5E5E0] px-3 py-2 text-[10px] text-[#A3A6A3] flex items-center gap-2">
            <Book className="w-3 h-3" />
            {filtered.length} contact{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}
            {contacts.length > filtered.length && ` sur ${contacts.length}`}
          </div>
        </PopoverContent>
      </Popover>

      {/* Chip contact lié — survol pour la mini-carte */}
      <LinkedContactChip contact={linkedContact} onUnlink={onUnlink} />
    </div>
  );
}

export default AddressBookPicker;
